import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { AuditDetail } from "../api/client";
import { useAuditDetail } from "../api/queries";

export function AuditDetailPage() {
  const search = useSearch({ from: "/app/audit/$id" });
  const nav = useNavigate({ from: "/app/audit/$id" });
  const { id } = useParams({ from: "/app/audit/$id" });

  const { data, ...result } = useAuditDetail(id);
  const item = data?.item;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ບັນທຶກການກວດກາ", to: "/app/audit" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ລາຍລະອຽດບັນທຶກການກວດກາ
            </h2>
            <p className="text-muted-foreground">ເບິ່ງລາຍລະອຽດຂອງບັນທຶກການກວດກາ.</p>
          </div>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/audit", search: { ...search } })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>
        <QueryState
          result={result}
          title="ກໍາລັງໂຫຼດບັນທຶກການກວດກາ"
          description="ກໍາລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!item ? null : (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <SummaryCard
                  variant="grid"
                  title="ພາບລວມ"
                  items={[
                    {
                      label: "ID",
                      children: (
                        <span className="break-all font-mono">{item.id}</span>
                      ),
                    },
                    {
                      label: "ເກີດຂຶ້ນ",
                      value:
                        item.occurredAt != null
                          ? formatDateTimeLocal(item.occurredAt)
                          : "-",
                    },
                    {
                      label: "ການກະທໍາ",
                      children: (
                        <Badge variant="secondary">{item.action}</Badge>
                      ),
                    },
                    {
                      label: "ຜົນລັບ",
                      children: (
                        <Badge
                          variant={
                            item.result === "failed" ? "destructive" : "success"
                          }
                        >
                          {item.result ?? "success"}
                        </Badge>
                      ),
                    },
                    {
                      label: "ຜູ້ກະທໍາ",
                      children: (
                        <span className="break-all font-mono">
                          {item.actorId ?? "-"}
                        </span>
                      ),
                    },
                    { label: "ບົດບາດ", value: item.actorRole ?? "-" },
                    {
                      label: "ວັດຖຸ",
                      value:
                        `${item.entityType ?? "-"} ${item.entityId ? `#${item.entityId}` : ""}`.trim(),
                    },
                    {
                      label: "ເສັ້ນທາງ",
                      children: (
                        <span className="break-all font-mono">
                          {item.method} {item.path}
                        </span>
                      ),
                    },
                    {
                      label: "ຄໍາຮ້ອງຂໍ",
                      children: (
                        <span className="break-all font-mono">
                          req={item.requestId ?? "-"} trace=
                          {item.traceId ?? "-"}
                        </span>
                      ),
                    },
                    {
                      label: "ລູກຄ້າ",
                      value: `ip=${item.ip ?? "-"} ua=${item.userAgent ?? "-"}`,
                    },
                    ...(item.error
                      ? [
                          {
                            label: "ຂໍ້ຜິດພາດ",
                            children: (
                              <span className="wrap-break-word text-destructive">
                                {item.error}
                              </span>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>

              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>ກ່ອນ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
                      {JSON.stringify(
                        (item as unknown as AuditDetail).before ?? null,
                        null,
                        2,
                      )}
                    </pre>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>ຫຼັງ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
                      {JSON.stringify(
                        (item as unknown as AuditDetail).after ?? null,
                        null,
                        2,
                      )}
                    </pre>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>ເມຕາ</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
                      {JSON.stringify(
                        (item as unknown as AuditDetail).meta ?? null,
                        null,
                        2,
                      )}
                    </pre>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
